









































































































































































































import { Vue, Component, Ref } from "vue-property-decorator";
import { vxm } from "@/store";
import L from "leaflet";
import { LMap, LTileLayer, LMarker, LCircle, LPolyline, LCircleMarker, LTooltip, LImageOverlay, LControl } from "vue2-leaflet";
import TileLayerGoogleMutant from "@/components/map/TileLayerGoogleMutant.vue";
import Task from "@/domain/task";
import { firestore } from "@/plugins/firebase";
import Team from "@/domain/team";
import { query, collection, orderBy, onSnapshot } from "@firebase/firestore";
import { Unsubscribe, deleteDoc, doc, limit, limitToLast, where } from "firebase/firestore";

@Component({
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LCircle,
    LPolyline,
    LCircleMarker,
    LTooltip,
    LImageOverlay,
    LControl,
    "tile-layer-googlemutant": TileLayerGoogleMutant
  }
})
export default class Map extends Vue {
  @Ref() readonly map!: L.Map;

  _selectedTeams: string[] = [];
  tracks: {
    [teamId: string]: {
      team: Team;
      latLngs: L.LatLng[];
      latestLatLng: L.LatLng | null;
      limit: number;
    };
  } = {};

  latestTeamLocations: { [teamId: string]: L.LatLng } = {};
  trackUpdates: { team: Team; latLng: L.LatLng }[] = [];
  trackSubscriptions: Unsubscribe[] = [];
  trackUpdateInterval = -1;
  mapContainer: HTMLElement | null = null;
  gestureEventOptions: AddEventListenerOptions = { passive: false };
  preventBrowserGestureZoom = (event: Event) => event.preventDefault();

  zoom = 0;
  selectedLayer = this.layers[0].type;
  showTeams = true;
  showTasks = true;

  mounted() {
    document.documentElement.classList.add("map-page-open");
  }

  get fluid() {
    return vxm.app.fluid;
  }

  get game() {
    return vxm.results.game;
  }

  get publicResults() {
    return this.game.publicResults.includes("map") || vxm.results.isInstructor;
  }

  get teams() {
    return vxm.results.teams;
  }

  get tasks() {
    return vxm.results.game.tasks;
  }

  // All map views that are available
  get layers() {
    return [
      { type: "native", name: "Google", icon: "mdi-google-maps" },
      {
        type: "google-custom",
        name: "Google custom",
        icon: "mdi-map-legend"
      },
      { type: "mapbox", name: "Mapbox", icon: "mdi-mapbox" },
      { type: "mapbox-outdoor", name: "Mapbox outdoor", icon: "mdi-terrain" },
      {
        type: "native-satellite",
        name: "Google satellite",
        icon: "mdi-satellite-variant"
      },
      {
        type: "google-satellite",
        name: "Google satellite",
        icon: "mdi-satellite-variant"
      },
      {
        type: "mapbox-satellite",
        name: "Mapbox satellite",
        icon: "mdi-satellite-variant"
      }
    ].filter(layer => !!this.game.mapTiles.find(mapTile => mapTile.type === layer.type));
  }

  get googleCustomStyles() {
    const style = this.game.mapTiles.find(mapTile => mapTile.type === "google-custom")?.style;

    if (!style || style === "undefined") {
      return [];
    }

    try {
      return JSON.parse(style);
    } catch {
      return [];
    }
  }

  get markers() {
    const tasksById: { [id: string]: Task } = {};

    for (const task of this.game.tasks) {
      tasksById[task.id] = task;
    }

    return this.game.rules
      .filter(rule => rule.type === "SHOW_TASK_ON_MAP" && rule.location)
      .map(({ taskId, location }, index) => {
        const task = tasksById[taskId];
        const iconHeight = Math.min(52, task.pin.iconSize ? task.pin.iconSize[1] : 52);
        const iconWidth = task.pin.iconSize ? (task.pin.iconSize[0] * iconHeight) / task.pin.iconSize[1] : 32;

        let tooltip: string | null = null;

        switch (this.game.pinInfo) {
          case "order":
            tooltip = `${index + 1}`;
            break;
          case "score":
            tooltip = `${task.difficulty}`;
            break;
        }

        return Object.freeze({
          id: task.id,
          latLng: L.latLng(location.lat, location.lng),
          radius: location.radius,
          areaColor: task.pin.areaColor,
          tooltip,
          icon: L.icon({
            iconUrl: task.pin.iconUrl,
            iconSize: [iconWidth, iconHeight],
            iconRetinaUrl: task.pin.original,
            iconAnchor:
              task.pin.iconAnchor && task.pin.iconSize
                ? L.point(task.pin.iconAnchor[0] * iconWidth, task.pin.iconAnchor[1] * iconHeight)
                : undefined
          })
        });
      });
  }

  get overlays() {
    return this.game.mapOverlays.map(overlay => ({
      url: overlay.imageUrl,
      bounds: L.latLngBounds(L.latLng(overlay.bounds.bottom, overlay.bounds.left), L.latLng(overlay.bounds.top, overlay.bounds.right))
    }));
  }

  mapReady() {
    this._selectedTeams = [];
    this.disableBrowserPinchZoom();

    // Initialize track subscriptions
    this.resetTrackSubscriptions();

    // Update tracks up to once per 5 seconds
    this.trackUpdateInterval = setInterval(() => this.applyTrackUpdates(), 5000) as any;

    // Fit markers in view
    this.fitTasksInBounds();
  }

  disableBrowserPinchZoom() {
    this.mapContainer = this.$el.querySelector<HTMLElement>(".leaflet-container");

    if (!this.mapContainer) {
      return;
    }

    this.mapContainer.addEventListener("gesturestart", this.preventBrowserGestureZoom, this.gestureEventOptions);
    this.mapContainer.addEventListener("gesturechange", this.preventBrowserGestureZoom, this.gestureEventOptions);
    this.mapContainer.addEventListener("gestureend", this.preventBrowserGestureZoom, this.gestureEventOptions);
  }

  resetTrackSubscriptions() {
    // Unsubscribe from previous subscriptions
    for (const unsubscribe of this.trackSubscriptions) {
      unsubscribe();
    }

    // Initialize and sync team tracks
    let snapshotsReceived = 0;

    // Create a track subscription for each visible team
    const numberOfTracks = this.teams.length;
    const numberOfSelectedTracks = this._selectedTeams.length;

    //this.tracks = {};

    this.trackSubscriptions = this.teams.map(team => {
      // Default limit is 5 if no teams are selected
      let limit = numberOfSelectedTracks ? 1 : 5;

      // If the team is explicitly selected, increase the limit
      if (this._selectedTeams.includes(team.id)) {
        limit = Math.ceil(10000 / numberOfSelectedTracks);
      }

      // Initialize track
      if (team.id in this.tracks) {
        this.tracks[team.id].limit = limit;
        this.tracks[team.id].latLngs = [];
      } else {
        this.tracks[team.id] = {
          team,
          latLngs: [],
          latestLatLng: null,
          limit
        };
      }

      const q = query(
        collection(firestore, `events/${vxm.results.eventId}/track`),
        where("teamId", "==", team.id),
        orderBy("time"),
        limitToLast(limit)
      );

      return onSnapshot(q, snapshot => {
        const changes = snapshot.docChanges();

        changes.forEach(change => {
          if (change.type !== "added") {
            return;
          }

          const { lat, lng } = change.doc.data();
          const latLng = Object.freeze(L.latLng(lat, lng));

          // Skip if last location is the same
          if (this.latestTeamLocations[team.id]?.equals(latLng)) {
            /* const d = doc(firestore, `events/${vxm.results.eventId}/track/${change.doc.id}`);
            deleteDoc(d).then(() => {
              console.log("Document successfully deleted!");
            }); */
            return;
          }

          this.trackUpdates.push({ team, latLng });
          this.latestTeamLocations[team.id] = latLng;
        });

        if (++snapshotsReceived === numberOfTracks) {
          this.applyTrackUpdates();
        }
      });
    });
  }

  applyTrackUpdates() {
    if (!this.trackUpdates.length) {
      return;
    }

    for (const { team, latLng } of this.trackUpdates) {
      const track = this.tracks[team.id];

      if (track) {
        track.latLngs.push(latLng);
        track.latestLatLng = latLng;

        // Force Vue to update the latLngs array and slice it to the limit
        track.latLngs = track.latLngs.slice(-track.limit);
      }
    }

    this.trackUpdates = [];

    // Force Vue to update the tracks object
    this.tracks = { ...this.tracks };
  }

  fitTasksInBounds() {
    if (this.markers.length) {
      try {
        this.map.fitBounds(this.markers.map(marker => marker.latLng) as any);
      } catch (err) {
        // Leaflet has a problem on initial fitBounds
      }
    }
  }

  destroyed() {
    document.documentElement.classList.remove("map-page-open");

    for (const unsubscribe of this.trackSubscriptions) {
      unsubscribe();
    }

    if (this.mapContainer) {
      this.mapContainer.removeEventListener("gesturestart", this.preventBrowserGestureZoom, this.gestureEventOptions);
      this.mapContainer.removeEventListener("gesturechange", this.preventBrowserGestureZoom, this.gestureEventOptions);
      this.mapContainer.removeEventListener("gestureend", this.preventBrowserGestureZoom, this.gestureEventOptions);
    }

    clearInterval(this.trackUpdateInterval);
  }

  get selectedTeams() {
    return this._selectedTeams;
  }

  set selectedTeams(teamIds: string[]) {
    this._selectedTeams = teamIds;
    this.resetTrackSubscriptions();
  }
}
